import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '@lib/supabase';
import { getApiBaseUrl } from '@lib/apiBase';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * WS6 operator console — consent-gated remote screen view + D-pad control.
 *
 * Flow: POST support/start (mints a session, asks the TV for consent) → open
 * /ws/support?session=… → render the customer's ~1fps JPEG frames + drive a
 * D-pad. Nothing renders until the customer APPROVES on their TV; the TV shows
 * a persistent banner the whole time and can End it; a 15-min time-box bounds
 * it. Frames are never persisted (here or server-side). See docs/REMOTE_SUPPORT.md.
 */
type Status = 'starting' | 'waiting' | 'active' | 'denied' | 'ended' | 'error';

export default function AdminSupport() {
  const { householdId } = useParams<{ householdId: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('starting');
  const [frame, setFrame] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');
  const wsRef = useRef<WebSocket | null>(null);
  const sessionRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    (async () => {
      if (!householdId) return;
      const start = await streamFlowApi.adminSupportStart(householdId);
      if (cancelled) return;
      if (!start.success || !start.data) {
        setStatus('error'); setNote(start.error?.message ?? 'Could not start session'); return;
      }
      sessionRef.current = start.data.session_id;
      setStatus('waiting');
      setNote(start.data.reached === 0
        ? 'No device is online for this household right now — waiting in case one connects.'
        : 'Waiting for the customer to approve on their TV…');
      const token = (await supabase.auth.getSession()).data.session?.access_token ?? '';
      const wsBase = getApiBaseUrl().replace(/^http/, 'ws');
      ws = new WebSocket(`${wsBase}/ws/support?token=${encodeURIComponent(token)}&session=${encodeURIComponent(start.data.session_id)}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        let m: { type?: string; data?: Record<string, unknown> };
        try { m = JSON.parse(ev.data as string); } catch { return; }
        if (m.type === 'support_consent') {
          setStatus(m.data?.approved ? 'active' : 'denied');
          if (!m.data?.approved) setNote('The customer declined the screen-view request.');
        } else if (m.type === 'support_frame' && typeof m.data?.jpeg === 'string') {
          setFrame(`data:image/jpeg;base64,${m.data.jpeg}`);
          setStatus((p) => (p === 'denied' || p === 'ended' ? p : 'active'));
        } else if (m.type === 'support_ended') {
          setStatus('ended');
          setNote(`Session ended${m.data?.reason ? ` (${String(m.data.reason)})` : ''}.`);
        }
      };
      ws.onclose = () => setStatus((p) => (p === 'denied' || p === 'error' ? p : 'ended'));
    })();
    return () => { cancelled = true; ws?.close(); };
  }, [householdId]);

  const sendKey = (key: string) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && status === 'active') {
      ws.send(JSON.stringify({ type: 'support_key', data: { key } }));
    }
  };

  const end = async () => {
    if (sessionRef.current) await streamFlowApi.adminSupportStop(sessionRef.current);
    wsRef.current?.close();
    navigate('/admin/streaming');
  };

  const Key = ({ label, k }: { label: string; k: string }) => (
    <button
      onClick={() => sendKey(k)}
      disabled={status !== 'active'}
      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-40"
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold">Remote support</h1>
        <button onClick={() => void end()} className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700">
          End session
        </button>
      </div>

      <div className={`mb-3 rounded-md border px-3 py-2 text-sm ${
        status === 'active' ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : status === 'denied' || status === 'error' ? 'border-rose-200 bg-rose-50 text-rose-900'
          : status === 'ended' ? 'border-gray-200 bg-gray-50 text-gray-700'
          : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
        <span className="font-medium capitalize">{status}</span>{note ? ` — ${note}` : ''}
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_auto]">
        <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-black">
          {frame && status === 'active'
            ? <img src={frame} alt="customer screen" className="h-full w-full object-contain" />
            : <span className="text-sm text-gray-400">
                {status === 'active' ? 'Waiting for first frame…' : 'No video (DRM playback shows blank by design).'}
              </span>}
        </div>

        <div className="flex flex-col items-center gap-2">
          <Key label="▲" k="DPAD_UP" />
          <div className="flex gap-2">
            <Key label="◀" k="DPAD_LEFT" />
            <Key label="OK" k="DPAD_CENTER" />
            <Key label="▶" k="DPAD_RIGHT" />
          </div>
          <Key label="▼" k="DPAD_DOWN" />
          <div className="mt-2 flex gap-2">
            <Key label="Back" k="BACK" />
            <Key label="Home" k="HOME" />
          </div>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        The customer sees a persistent “GASTV Support is viewing your screen” banner and can end this anytime.
        Sessions auto-expire after 15 minutes. Protected video renders blank.
      </p>
    </div>
  );
}
